import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextField, Autocomplete } from "@mui/material";
import { memo } from "react";
import { NumberField } from "@base-ui/react/number-field";

import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { GeoCrushingLoaderDTO } from "../../DTOs/Geology/GeoCrushingLoaderDTO";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";

interface InsertGeoCrushingRowProps {
  state: GeoRehandleDTO;
  setState: React.Dispatch<React.SetStateAction<GeoRehandleDTO>>;
  onAdd: () => Promise<boolean>;
  loaderOptions: GeoCrushingLoaderDTO[];
  isLoaderOptionsLoading: boolean;
  crushingMovementFromLocationOptions: LocationDTO[];
  isCrushingMovementFromLocationOptionsLoading: boolean;
  crushingMovementToLocationOptions: LocationDTO[];
  isCrushingMovementToLocationOptionsLoading: boolean;
  isShiftLocked: boolean
}

export const InsertGeoCrushingRow = memo(function InsertRow(
  {
    state,
    setState,
    onAdd,
    loaderOptions,
    isLoaderOptionsLoading,
    crushingMovementFromLocationOptions,
    isCrushingMovementFromLocationOptionsLoading,
    crushingMovementToLocationOptions,
    isCrushingMovementToLocationOptionsLoading,
    isShiftLocked
  }: InsertGeoCrushingRowProps) {
  return (
    <ul className="py-1 flex gap-2 border-b border-theme-border">
      <li key="loader" className="basis-[20%]">
        <Autocomplete
          classes={{ root: 'w-full' }}
          size="small"
          loading={isLoaderOptionsLoading}
          disabled={isLoaderOptionsLoading || isShiftLocked}
          options={loaderOptions}
          getOptionLabel={(option) => option.plantRef}
          value={loaderOptions.find(x => x.operationId === state.operationId) ?? null}
          isOptionEqualToValue={(option, value) => option.operationId === value.operationId}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({
                ...prev,
                bucketTypeId: newValue.bucketTypeId,
                plantId: newValue.plantId,
                operationId: newValue.operationId
              }));
            }
          }}
          renderInput={(params) => <TextField
            {...params}
            label="Loader"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isLoaderOptionsLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />}
        />
      </li>
      <li key="movement-from" className="basis-[30%]">
        <Autocomplete
          disabled={isShiftLocked}
          classes={{ root: 'w-full' }}
          size="small"
          loading={isCrushingMovementFromLocationOptionsLoading}
          options={crushingMovementFromLocationOptions}
          value={crushingMovementFromLocationOptions.find(x => x.id === state.movementFromId) ?? null}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, movementFromId: newValue.id }));
            }
          }}
          renderInput={(params) => <TextField
            {...params}
            label="Movement From"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {(isCrushingMovementFromLocationOptionsLoading) && <FontAwesomeIcon icon={faSpinner} spin />}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />}
        />
      </li>
      <li key="loads" className="basis-[10%] text-[0.9rem]">
        <NumberField.Root
          disabled={isShiftLocked}
          value={state.loads ?? null}
          onValueChange={(val) => {
            if (val === null || val === state.loads) return;
            setState(prev => ({ ...prev, loads: val ?? 0 }));
          }}
          className="h-full bg-theme-surface-2"
        >
          <NumberField.Group className="relative flex gap-0.5 h-full border border-theme-border rounded-sm">
            <NumberField.Decrement />
            <NumberField.Input
              placeholder="Loads"
              className={`h-full w-full px-1 py-2 bg-theme-surface-2 text-theme-text disabled:text-theme-text-muted transition`}
            />
            <NumberField.Increment />
          </NumberField.Group>
        </NumberField.Root>
      </li>
      <li key="movement-to" className="basis-[30%]">
        <Autocomplete
          disabled={isShiftLocked}
          classes={{ root: 'w-full' }}
          size="small"
          loading={isCrushingMovementToLocationOptionsLoading}
          options={crushingMovementToLocationOptions}
          value={crushingMovementToLocationOptions.find(x => x.id === state.movementToId) ?? null}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, movementToId: newValue.id }));
            }
          }}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <span className="text-theme-danger-light">{option.name}</span>
              </li>
            )
          }}
          renderInput={(params) => {
            return <TextField
              {...params}
              label="Movement To"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  style: {
                    color: "red",
                    transition: "color 0.2s ease-in-out"
                  },
                  endAdornment: (
                    <>
                      {(isCrushingMovementToLocationOptionsLoading) && <FontAwesomeIcon icon={faSpinner} spin />}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          }}
        />
      </li>

      <li className="basis-[10%]">
        <button
          disabled={isShiftLocked}
          type="button"
          onClick={onAdd}
          className="px-4 py-2 rounded bg-theme-info hover:bg-theme-info-hover cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition duration-300 text-white rounded-sm border-none text-sm font-bold"
        >
          Add
        </button>
      </li>
    </ul>
  );
});